/**
 * Custom ID number generator
 *
 * Code uses a custom implementation of Verhoeff's dihedral algorithm.
 * Function takes 6 base digits and adds 4 check digits to create customer ID number.
 *
 * Returns value in the format: 4E3-FDC2-B1A
 *
 * Where:
 * ABCDEF are base digits composed of:
 *  ABCD: four random digits
 *  E: week number reduced to one digit
 *  F: client public IP address reduced to one digit
 * 1234 are check digits
 *
 *
 * Steps:
 * 1. Get base digits
 *  1.1 Get user's public IP address by making an API request to https://api.ipify.org
 *  1.2 Reduce received IP address to a one-digit number by adding its digits recursively – this is base digit 'F'
 *  1.3 Get current ISO week number in year and reduce it to a one-digit number by adding its digits recursively - this is base digit 'E'
 *  1.4 Generate a four-digit random number – these are base digits 'ABCD'
 *
 * 2. Generate check digits
 * Each check digit is obtained by using Verhoeff's dihedral algorithm. The input is a combination of base digits and other check digits, and the output is always a one-digit check number. The four check digits are generated as follows:
 *  2.1 Check digit 1: ABC => 1
 *  2.2 Check digit 2: A1BCDFE => 2
 *  2.3 Check digit 3: A1B2CDF => 3
 *  2.4 Check digit 4: E3FDC2B1A => 4
 *  2.5 Final generated number takes the following order: 4E3FDC2B1A
 *
 * 3. Format and return final result
 *  3.1 Take 4E3FDC2B1A and hyphenate it to 4E3-FDC2-B1A for readability
 *  3.2 Return 4E3-FDC2-B1A as final output
 */

declare global {
  interface Window { digitalData: any; }
}

export const getCheckDigit = (baseInput: string) => {
  const d = [
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
    [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
    [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
    [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
    [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
    [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
    [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
    [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
    [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
  ];
  const inv = [0, 4, 3, 2, 1, 5, 6, 7, 8, 9];
  const p = [
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
    [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
    [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
    [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
    [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
    [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
    [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
  ];

  let x = 0;

  baseInput.split('').forEach((item: string, i: number) => {
    x = d[x][p[(i + 1) % 8][baseInput.charCodeAt(i) - 48]];
  });

  return inv[x];
};

export const getISOWeekNumber = (date: Date) => {
  const shadowDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  shadowDate.setUTCDate(shadowDate.getUTCDate() + 4 - (shadowDate.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(shadowDate.getUTCFullYear(), 0, 1));
  return Math.ceil((((Number(shadowDate) - Number(yearStart)) / 86400000) + 1) / 7);
};

const getRandomNumber = (digits: number) => {
  const units = 10 ** (digits - 1);
  return String((Math.floor(Math.random() * (9 * units)) + units));
};

export const reduceDigits = (input: number): number => {
  if (input >= 10) {
    return reduceDigits(String(input).split('').map(Number).reduce((a, b) => a + b, 0));
  }
  return input;
};

export const getReducedIP = (ip: string) => String(reduceDigits(Number(ip.split('.').join(''))));

export const getCurrentWeekDigit = (date: Date) => String(reduceDigits(getISOWeekNumber(date)));

export const encode = (input: string) => {
  const chars = input.split('');
  const a = chars[0];
  const b = chars[1];
  const c = chars[2];
  const d = chars[3];
  const e = chars[4];
  const f = chars[5];
  const one = getCheckDigit(a + b + c);
  const two = getCheckDigit(a + one + b + c + d + f + e);
  const three = getCheckDigit(a + one + b + two + c + d + f);
  const four = getCheckDigit(e + three + f + d + c + two + b + one + a);

  return four + e + three + f + d + c + two + b + one + a;
};

export const getPart = (collection: string[], start: number, end?: number) => collection.slice(start, end).join('');

export const hyphenate = (input: string) => {
  const digits = input.split('');
  return [getPart(digits, 0, 3), getPart(digits, 3, 7), getPart(digits, 7)].join('-');
};

const generateNumber = (response: string) => {
  const base = getRandomNumber(4) + getCurrentWeekDigit(new Date()) + getReducedIP(response);
  const customerNumber = hyphenate(encode(base));
  const dataLayer = window.digitalData;

  if (dataLayer && dataLayer.page) {
    dataLayer.page.tools = {
      evalue: {
        number: customerNumber,
        timestamp: Date.now(),
      },
    };
  }

  return customerNumber;
};

export default generateNumber;



// WEBPACK FOOTER //
// ./src/utilities/customerNumber.ts